<template>
    <modals-generic
    :sizeClass="'medium'"
    :headerColor="headerColor"
    :title="selectedPolicy.insuranceDescription"
    :subTitle="selectedPolicy.policyStatus.description"
    :icon="headerTitleIcon"
    :tabs="tabsList"
    :preventModalClose="true"
    @tabClicked="tabClickedHandler"
    @hideMe="emit('hideMe')">
        <div class="policy-details" v-if="!showSpinner && !applyErrorState" :class="{'mobile': getMobileAppState}">
            <!-- Information Tab -->
            <component
            v-if="tabsList[0].selected"
            :is="policyComponent"
            :selectedPolicy="selectedPolicy"
            :policyDetails="policyDetails"/>
            <!-- Receipts Tab -->
            <table-generic
            v-if="tabsList[1].selected && receiptsTableData.length"
            :headerData="receiptsHeaderData"
            :tableStyles="receiptsTableStyles"
            :tableData="receiptsTableData"
            @rowClicked="receiptClickHandler($event)"/>
        </div>
        <!-- Loader Generic -->
        <loaders-generic v-if="showSpinner" fullSize/>
        <!-- Detail Error State -->
        <empty-state-error v-if="applyErrorState && !showSpinner" :message="t('bankassurance.policies.details.policyInfo.policyInfoError')"/>
    </modals-generic>
    <!-- Error Feedback -->
    <toasts-generic
    v-if="showErrorToast"
    toastType="error"
    :feedbackText="errorToastData.text"
    :buttonsParams="errorToastData.buttons"
    @hideMe="showErrorToast = false"/>
    <!-- Receipt Details Modal -->
	<receipt-details-modal
	v-if="showReceitDetailsModal"
    :selectedPolicy="selectedPolicy"
	:selectedReceipt="selectedReceipt"
	@hideMe="showReceitDetailsModal = false"/>
</template>

<script setup>
import ModalsGeneric from '@/components/generic/modals/side/Generic'
import LoadersGeneric from '@/components/generic/loaders/Generic'
import ToastsGeneric from '@/components/generic/toasts/Generic'
import EmptyStateError from '@/components/generic/emptyStates/Error'
import TabsGeneric from '@/components/generic/tabs/Generic'
import TableGeneric from '@/components/generic/table/Generic'
import ReceiptDetailsModal from '@/components/features/bankassurance/policies/ReceiptDetailModal'

/* Vue & Helpers */
import { ref, computed, defineAsyncComponent } from 'vue'
import { titleCaseBuilder } from '@/assets/scripts/strings'
import { policyBackgroundColor, policyStatusIcon, receiptsStatusIcon, receiptsIconColorMapper } from '@/assets/scripts/mappers/policies'
import { getDate } from '@/assets/scripts/dateBuilders'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapActions & mapGetters */
const { requestPolicyDetails, requestPolicyReceiptsList } = mapActions(moduleMapper.BANKASSURANCE_POLICIES)
const { getPolicyReceiptsList } = mapGetters(moduleMapper.BANKASSURANCE_POLICIES)
const { getMobileAppState } = mapGetters()

/* Emmitters */
const emit = defineEmits(['hideMe', 'updateCategory'])

const props = defineProps({
    selectedPolicy: Array
})

/* Data */
const showSpinner = ref(false)

/*************************************************
*                                                *
*                     Modal                      *
*                                                *
*************************************************/
const policyDetails = ref([])
const showReceitDetailsModal = ref(false)
const selectedReceipt = ref(null)

const headerColor = computed(() => {
    return policyBackgroundColor(props.selectedPolicy.policyStatus.code)
})

const headerTitleIcon = computed(() => {
    return policyStatusIcon(props.selectedPolicy.policyStatus.code)
})

const policyComponent = computed(() => {
    const componentName = titleCaseBuilder(props.selectedPolicy.insuranceCode.toLowerCase())

    return defineAsyncComponent(() => import('@/components/features/bankassurance/policies/' + componentName + 'PolicyInfo'))
})

/*************************************************
*                                                *
*                 Details Tabs                   *
*                                                *
*************************************************/
const tabsList = ref([
	{
        id: 1,
        label: t('bankassurance.policies.details.tabs.informations'),
        selected: true
    },
	{
        id: 2,
        label: t('bankassurance.policies.details.tabs.receipts'),
        selected: false
    }
])

const tabClickedHandler = event => {
    tabsList.value.forEach(tab => {
		tab.selected = tab.id === event
	})
}

/*************************************************
*                                                *
*               Receipts Table                   *
*                                                *
*************************************************/
const receiptsHeaderData = computed(() => {
    if (getMobileAppState.value) {
        return [
            { label: t('bankassurance.policies.details.tableHeader.receiptNumber'), showInMobile: { groupWith: ['amount', 'date'] }, dataAttr: 'receiptNumber', rowCellType: 'title' },
            { label: t('bankassurance.policies.details.tableHeader.amount'), dataAttr: 'amount', rowCellType: 'number' },
            { label: t('bankassurance.policies.details.tableHeader.date'), dataAttr: 'date', rowCellType: 'date' },
            { label: t('bankassurance.policies.details.tableHeader.situation'), showInMobile: true, dataAttr: 'situation', rowCellType: 'status' },
            { label: '', showInMobile: true, dataAttr: 'details', rowCellType: 'status' }
        ]
    }

    return [
        { label: t('bankassurance.policies.details.tableHeader.receiptNumber'), dataAttr: 'receiptNumber', rowCellType: 'title' },
        { label: t('bankassurance.policies.details.tableHeader.date'), dataAttr: 'date', rowCellType: 'date' },
        { label: t('bankassurance.policies.details.tableHeader.amount'), dataAttr: 'amount', rowCellType: 'number' },
        { label: t('bankassurance.policies.details.tableHeader.situation'), dataAttr: 'situation', rowCellType: 'status' }
    ]
})

const receiptsTableStyles = computed(() => {
    if (getMobileAppState.value) {
        return [
            null,
            null,
            { 'text-align': 'end' },
            { 'text-align': 'end' },
            null
        ]
    }
    return [
        { width: '100px', 'max-width': '100px' },
        { width: '170px', 'max-width': '170px' },
        { 'text-align': 'end' },
        null
    ]
})

const receiptsTableData = computed(() => {
    if (getMobileAppState.value) {
        return getPolicyReceiptsList.value.map(receipt => {
            return [
                { value: receipt.receiptNumber, dataAttr: 'id' },
                {
                    type: 'title',
                    value: getMobileAppState.value ? `Nº ${receipt.receiptNumber}` : receipt.receiptNumber,
                    dataAttr: 'title'
                },
                {
                    type: 'amount',
                    value: { content: receipt.totalValue.value, currency: receipt.totalValue.currency, noBalanceIcon: true, hasArrowIcons: false },
                    dataAttr: 'amount'
                },
                {
                    type: 'description',
                    value: {
                        content: t('bankassurance.policies.details.policyInfo.receiptDate', { from: getDate(receipt.dateFrom), to: getDate(receipt.dateTo) }),
                        classes: 'capitalize'
                    },
                    dataAttr: 'date'
                },
                {
                    type: 'status',
                    value: {
                        text: receipt.situation || '',
                        icon: receiptsStatusIcon(receipt.situationId),
                        classes: receiptsIconColorMapper(receipt.situationId)
                    },
                    dataAttr: 'situation'
                },
                {
                    type: 'status',
                    value: {
                        icon: 'chevron-down',
                        classes: 'rotate-icon'
                    },
                    dataAttr: 'details'
                }
            ]
        })
    }

	return getPolicyReceiptsList.value.map(receipt => {
		return [
			{ value: receipt.receiptNumber, dataAttr: 'id' },
			{
				type: 'title',
				value: getMobileAppState.value ? `Nº ${receipt.receiptNumber}` : receipt.receiptNumber,
				dataAttr: 'title'
			},
			{
				type: 'description',
				value: {
                    content: t('bankassurance.policies.details.policyInfo.receiptDate', { from: getDate(receipt.dateFrom), to: getDate(receipt.dateTo) }),
                    classes: 'capitalize'
				},
				dataAttr: 'date'
			},
			{
                type: 'amount',
				value: { content: receipt.totalValue.value, currency: receipt.totalValue.currency, noBalanceIcon: true, hasArrowIcons: false },
				dataAttr: 'amount'
			},
			{
				type: 'status',
				value: {
					text: receipt.situation || '',
					icon: receiptsStatusIcon(receipt.situationId),
					classes: receiptsIconColorMapper(receipt.situationId)
				},
				dataAttr: 'situation'
			}
		]
	})
})

const receiptClickHandler = row => {
	selectedReceipt.value = getPolicyReceiptsList.value.find(receipt => receipt.receiptNumber === row.rowId)
	showReceitDetailsModal.value = true
}

/*************************************************
*                                                *
*                 Error Feedback                 *
*                                                *
*************************************************/
const showErrorToast = ref(false)
const applyErrorState = ref(false)

const errorToastData = ref({
	text: '',
	buttons: [{
        text: t('commonButtons.ignore'),
        callbackAction: () => { showErrorToast.value = false }
    },
    {
        text: t('commonButtons.tryAgain'),
        callbackAction: ''
    }]
})

/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const requestDetails = async () => {
	showSpinner.value = true
	showErrorToast.value = false
    applyErrorState.value = false

	try {
        const response = await requestPolicyDetails(props.selectedPolicy.insuranceId)
        await requestPolicyReceiptsList(props.selectedPolicy.insuranceId)
        policyDetails.value = response.data.data
        showSpinner.value = false
	} catch (error) {
		if (error.toString() === 'Cancel') return
		showSpinner.value = false
		errorHandler(error.response, requestDetails)
	}
}

const errorHandler = (data, retryFn) => {
	errorToastData.value.text = data && data.message ? data.message : t('genericErrorMessage')
	errorToastData.value.buttons[1].callbackAction = retryFn
	showErrorToast.value = true
    applyErrorState.value = true
}

requestDetails()
</script>

<style lang="scss" scoped>
.policy-details {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 2px;
    gap: 24px;

    &.mobile {
        width: calc(100% - 32px);
        margin-left: auto;
        margin-right: auto;
    }
}
</style>
